import { customAlphabet } from "nanoid";
import { toKebabCase } from "remeda";
import { z } from "zod";

const charset = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
export const nanoid = customAlphabet(charset);

const prefixes = {
	user: "usr",
	account: "acc",
	session: "ses",
	verification: "ver",
	"rate-limit": "rat",
	organization: "org",
	member: "mem",
	invitation: "inv",
	jwks: "jwk",
	passkey: "psk",
	"two-factor": "tfa",
	campaign: "cam",
	"campaign-image": "cai",
	"campaign-action": "caa",
	world: "wld",
	release: "rel",
	slot: "slo",
	image: "img",
	event: "evt",
	"report-reason": "rrs",
	track: "trk",
	atlas: "atl",
} as const;

export const reversePrefixes = Object.fromEntries(Object.entries(prefixes).map(([key, value]) => [value, key])) as Record<string, IdType>;

export type IdType = keyof typeof prefixes;
export type IdPrefix = typeof prefixes[IdType];

export type Id = `${IdPrefix}_${string}`;
export type IdOf<T extends IdType> = `${typeof prefixes[T]}_${string}`;

export const idLength = 24;

export function newId<T extends IdType>(type: T): IdOf<T> {
	const prefix = prefixes[type];
	const length = idLength - prefix.length - 1;

	return [prefix, nanoid(length)].join("_") as IdOf<T>;
}

/**
 * A deterministic ID generator, practically a hash function that conforms to the ID format.
 */
export async function newDeterministicId<T extends IdType>(type: T, value: string): Promise<IdOf<T>> {
	const prefix = prefixes[type];
	const length = idLength - prefix.length - 1;

	const sha = new Uint8Array(await crypto.subtle.digest("sha-256", new TextEncoder().encode(value)));

	const bytes = Array.from(sha.slice(0, length))
		.map((byte) => charset[byte % charset.length])
		.join("");

	return [prefix, bytes].join("_") as IdOf<T>;
}

const slugLength = 16;
const suffixLength = 8;

function trim(value: string, character: string = " ") {
	return value.replace(new RegExp(`^${character}+|${character}+$`, "g"), "");
}

export function newSlug(name: string, random: boolean = false, length: number = slugLength): string {
	const suffix = nanoid(length);
	const prefix = trim(toKebabCase(name).slice(0, length - suffixLength), "-");

	return `${prefix}${random ? `-${suffix}` : ""}`.toLowerCase().slice(0, length);
}

export function isId<T extends IdType>(type: T, value: string): value is IdOf<T> {
	if (value.length !== idLength) return false;

	const prefix = prefixes[type];
	const [a, b] = value.split("_", 2);

	if (a !== prefix || b?.length !== idLength - prefix.length - 1) return false;

	return true;

	// return b.match(new RegExp(`^[${charset}]+$`)) !== null;
}

export function getIdType(value: string): IdType | null {
	const [prefix] = value.split("_", 1);
	if (!prefix) return null;

	return reversePrefixes[prefix] || null;
}

export function idSchema<T extends IdType>(type: T) {
	return z
		.string()
		.refine((value) => isId(type, value), {
			message: `Invalid ${type} reference`,
		})
		.transform((value) => value as IdOf<T>);
}
